import {useMemo} from "react";
import moment from "moment";
import {
    GetHolidaysByCompanyAndDateRangeQuery,
    StateCode,
} from "../../../generated/graphql";

type Holiday = GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number];

export const getHolidaysToRender = ({
    date,
    holidays,
}: {
    date: Date;
    holidays: GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"];
}):
    | GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"]
    | undefined => {
    // return undefined;

    // Memoize the computation to avoid unnecessary recalculations
    const holidaysToRender = () => {
        // Define start and end of the day using moment
        const dayStart = moment(date).startOf("day");
        const dayEnd = moment(date).endOf("day");

        // Filter holidays that include the given date
        const allHolidaysOfDate = holidays.filter((holiday) => {
            const holidayStart = moment(holiday.start).startOf("day");
            const holidayEnd = moment(holiday.end).endOf("day");

            return (
                dayStart.isSameOrAfter(holidayStart) &&
                dayEnd.isSameOrBefore(holidayEnd)
            );
        });

        // Categorize holidays based on stateCode
        const {
            customHolidays,
            publicHolidays,
            schoolHolidays,
        } = allHolidaysOfDate.reduce(
            (acc, holiday) => {
                if (holiday.stateCode === StateCode.Custom) {
                    acc.customHolidays.push(holiday);
                } else if (holiday.stateCode === StateCode.National) {
                    acc.publicHolidays.push(holiday);
                } else {
                    acc.schoolHolidays.push(holiday);
                }

                return acc;
            },
            {
                customHolidays: [] as Array<Holiday>,
                publicHolidays: [] as Array<Holiday>,
                schoolHolidays: [] as Array<Holiday>,
            },
        );

        // Determine if multiple National holidays and at least one non-Custom holiday exist
        const shouldExcludeSchoolHolidays =
            publicHolidays.length > 1 && schoolHolidays.length > 0;

        let filteredSchoolHolidays: Array<Holiday> = [];

        if (!shouldExcludeSchoolHolidays) {
            // Create a Set for efficient lookup of public holidays by start and end dates
            const publicHolidaysSet = new Set(
                publicHolidays.map(
                    (holiday) =>
                        `${moment(holiday.start).format("YY-MM-DD")}_${moment(
                            holiday.end,
                        ).format("YY-MM-DD")}`,
                ),
            );

            // Filter out school holidays that overlap with public holidays
            filteredSchoolHolidays = schoolHolidays.filter(
                (schoolHoliday) =>
                    !publicHolidaysSet.has(
                        `${moment(schoolHoliday.start).format(
                            "YY-MM-DD",
                        )}_${moment(schoolHoliday.end).format("YY-MM-DD")}`,
                    ),
            );
        }

        // Combine all relevant holidays
        const combinedHolidays = [
            ...filteredSchoolHolidays, // Will be empty if shouldExcludeSchoolHolidays is true
            ...publicHolidays,
            ...customHolidays,
        ];

        // Sort holidays: custom holidays appear last
        combinedHolidays.sort((a, b) => {
            if (
                a.stateCode === StateCode.Custom &&
                b.stateCode !== StateCode.Custom
            ) {
                return 1;
            }
            if (
                a.stateCode !== StateCode.Custom &&
                b.stateCode === StateCode.Custom
            ) {
                return -1;
            }

            return 0;
        });

        return combinedHolidays;
    };

    return holidaysToRender();
};

// Function to convert hex to RGB
const hexToRgb = (hex: string | Array<any>) => {
    let r = 0;
    let g = 0;
    let b = 0;

    // 3 digits
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    }
    // 6 digits
    else if (hex.length === 7) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
    }

    return {r, g, b};
};

// Function to convert RGB to HSL
const rgbToHsl = (r, g, b) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    (r /= 255), (g /= 255), (b /= 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h;
    let s;
    const l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;

        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }

    return {h, s, l};
};

// Function to convert HSL to RGB
const hslToRgb = (h: number | undefined, s: number, l: number) => {
    let r;
    let g;
    let b;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;

            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255),
    };
};

// Function to convert RGB to Hex
const rgbToHex = (r: number, g: number, b: number) => {
    const toHex = (c: number) => {
        const hex = c.toString(16);

        return hex.length === 1 ? "0" + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Function to lighten a color
export const lightenColor = (color: string | Array<any>, percent: number) => {
    const {r, g, b} = hexToRgb(color);
    let {h, s, l} = rgbToHsl(r, g, b);

    l += percent / 100; // Increase lightness by the percentage
    l = Math.min(1, Math.max(0, l)); // Ensure l is between 0 and 1
    const {r: newR, g: newG, b: newB} = hslToRgb(h, s, l);

    return rgbToHex(newR, newG, newB);
};

// // Usage
// const color = holiday?.color ?? holiday?.holidayStateColor ?? "#000000";
// const lightenedColor = lightenColor(color, 20); // Lighten by 20%

// const badgeStyleHoliday = {
//     backgroundColor: lightenedColor,
//     borderBottom: `3px solid ${lightenedColor}`,
// };
